import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Box,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import { FaSteam } from "react-icons/fa";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { styled } from "@mui/material/styles";

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  color: theme.palette.text.secondary,
}));

const ConnectionCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
  marginBottom: theme.spacing(3),
}));

const Settings = () => {
  const [steamInfo, setSteamInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSteamInfo = async () => {
      try {
        const response = await axios.get(`/api/db/getsteaminfo`, {
          withCredentials: true,
        });
        setSteamInfo(response.data);
      } catch (error) {
        console.error("Failed to fetch Steam info:", error);
        setError("Failed to load Steam connection information");
      } finally {
        setLoading(false);
      }
    };

    fetchSteamInfo();
  }, []);

  const handleConnectSteam = () => {
    if (!steamInfo?.steam_profile) {
      const token = localStorage.getItem("discord_jwt");
      window.location.href = `${process.env.REACT_APP_API_URL}/auth/steam?token=${token}`;
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const isConnected = steamInfo?.steam_profile;

  return (
    <StyledContainer maxWidth="md" sx={{ marginLeft: 0 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Account Settings
      </Typography>

      {/* <Alert severity="warning" sx={{ mb: 3 }}>
        <strong>Important Notice:</strong> Due to a temporary global issue with
        Steam's authorization system, you cannot currently connect your Steam
        account automatically. To continue with your PvE Whitelist request,
        please open a support ticket on our Discord server so an admin can
        manually review and link your account. We apologize for the
        inconvenience and appreciate your patience!
      </Alert> */}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <ConnectionCard sx={{ backgroundColor: "#282828", width: "100%" }}>
        <Stack spacing={3}>
          <div>
            <SectionHeader variant="h6" component="h2">
              Connected Accounts
            </SectionHeader>
            <Typography variant="body1" color="text.secondary">
              Link your Steam account to request PvE Whitelist.
            </Typography>
          </div>

          <Divider />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <FaSteam size={32} />
              <div>
                <Typography variant="subtitle1">Steam</Typography>
                {isConnected && (
                  <Typography variant="body2" color="text.secondary">
                    Connected since{" "}
                    {new Date(steamInfo.created_at).toLocaleDateString()}
                  </Typography>
                )}
              </div>
            </Stack>

            {isConnected ? (
              <Chip
                label="Connected"
                color="success"
                icon={<CheckCircleIcon />}
                variant="outlined"
              />
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={<FaSteam />}
                onClick={handleConnectSteam}
                sx={{ textTransform: "none" }}
              >
                Connect Steam Account
              </Button>
            )}
          </Stack>
          {isConnected && (
            <Alert severity="info" sx={{ mb: 2, width: "100%" }}>
              Your Steam account is successfully connected. You can now request
              PvE Whitelist via Viktor bot.
            </Alert>
          )}
        </Stack>
      </ConnectionCard>
    </StyledContainer>
  );
};

export default Settings;
